import { AuditTrailAction } from '../../audit/domain/auditTrailAction';
import { DashboardComponentType } from '../../dashboards/domain/dashboardComponentType';
import { LogSorterColumnPlacement } from '../../logSorter/domain/logSorterBinLayout';
import { LogSorterNumberSequence } from '../../logSorter/domain/logSorterNumberSequence';
import { Anchor } from '../../map/domain/anchor';
import { ProductionOrderState } from '../../productionOrders/domain/productionOrderState';
import { ProductType } from '../../products/domain/productType';
import { ReportType } from '../../reports/domain/reportType';
import { ShapeMode } from '../../shapes/domain/shapeMode';
import { StorageAreaStatus } from '../../storageAreas/domain/storageAreaStatus';
import { StorageAreaType } from '../../storageAreas/domain/storageAreaType';
import { VehicleType } from '../../vehicles/domain/vehicleType';
import { VehicleSymbol } from '../../vehicles/domain/vehicleSymbol';
import { KeyboardActions } from '../../vehicles/domain/keyboardActions';
import { Unit } from '../../common/domain/bulkUnits';
import { DeliveryStatus } from '../../deliveries/domain/deliveryStatus';

export const english = {
  generic: {
    active: 'Active',
    language: 'Language',
    yes: 'Yes',
    no: 'No',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    id: 'Id',
    name: 'Name',
    description: 'Description',
    type: 'Type',
    code: 'Code',
    quality: 'Quality',
    columns: 'Columns',
    color: 'Color',
    capacity: 'Capacity',
    loadingTableData: 'Loading...',
    search: 'Search',
    generalErrorMsg: 'Ops! Something went wrong',
    signIn: 'Sign in',
    signOut: 'Sign out',
    width: 'Width',
    length: 'Length',
    resetColumns: 'Reset columns',
    height: 'Height',
    login: 'Login',
    logout: 'Logout',
    next: 'Next',
    back: 'Back',
    abort: 'Abort',
    close: 'Close',
    searchBy: 'Search by',
    clear: 'Clear',
    error: 'Error',
    success: 'Success',
    updated: 'Updated',
    added: 'Added',
    taskCompleted: 'Task completed',
    created: 'Created',
    deleted: 'Deleted',
    toggle: 'Toggle',
    status: 'Status',
    position: 'Position',
    direction: 'Direction',
    anchor: 'Anchor',
    product: 'Product',
    vehicle: 'Vehicle',
    map: 'Map',
    satellite: 'Satellite',
    settings: 'Settings',
    geofence: 'Geofence',
    geofenceTop: 'Geofence top',
    geofenceBottom: 'Geofence bottom',
    geofenceLeft: 'Geofence left',
    geofenceRight: 'Geofence right',
    storageArea: 'Storage area',
    inventory: 'Inventory',
    noAvailableOptions: 'No available options',
    edit: 'Edit',
    select: 'Select',
    default: 'Default',
    mirrored: 'Mirrored',
    add: 'Add',
    top: 'Top',
    bottom: 'Bottom',
    left: 'Left',
    right: 'Right',
    longitude: 'Longitude',
    latitude: 'Latitude',
    rotation: 'Rotation',
    shape: 'Shape',
    inside: 'Inside',
    outside: 'Outside',
    logSorter: 'Log sorter',
    confirm: 'Confirm',
    on: 'On',
    off: 'Off',
    circle: 'Circle',
    polygon: 'Polygon',
    size: 'Size',
    rotate: 'Rotate',
    freeform: 'Freeform',
    rectangle: 'Rectangle',
    empty: 'Empty',
    discard: 'Discard',
    audit: 'Audit',
    export: 'Export',
    import: 'Import',
    take: 'Take',
    ok: 'Ok',
    from: 'From',
    to: 'To',
    move: 'Move',
    coil: 'Coil',
    swap: 'Swap',
    copy: 'Copy',
    copyOfName: (name: string) => `${name} (Copy)`,
    pick: 'Pick',
    unknown: 'Unknown',
    enabled: 'Enabled',
    note: 'Note',
    groups: 'Groups',
    productGroup: 'Product group',
    products: 'Products',
    numberOfLogs: 'Number of logs',
    balance: 'Balance',
    fifo: 'FIFO',
    volume: 'Volume',
    numberOfProductsInStorage: 'Number of products',
    storageAreas: 'Storage areas',
    showMore: 'Show more',
    preview: 'Preview',
    selectColumns: 'Select columns',
    locked: 'Locked',
    unlocked: 'Unlocked',
    keybindings: 'Keybindings',
    productionOrder: 'Production order',
    resource: 'Resource',
    priority: 'Priority',
    amount: 'Amount',
    newVersion: 'New version of SILog',
    important: 'Important',
    noData: 'No data',
    total: 'Total',
    calculate: 'Calculate',
    sorted: 'Sorted',
    unsorted: 'Unsorted',
    unit: 'Unit',
    content: 'Content',
    inTotal: 'in total',
    reset: 'Reset',
    resetToDefault: 'Reset to default',
    resetLayoutConfirmHeader: 'Reset layout',
    resetLayoutConfirmMessage:
      'Reset the dialog layout to its default arrangement? The window size and position are kept.',
    customer: 'Customer',
    customerReference: 'Customer reference',
    user: 'User',
    weight: 'Weight',
    filter: 'Filter'
  },
  application: {
    exit: 'Exit',
    exitSilog: 'Exit SILog',
    confirmExit: 'Are you sure you want to exit SILog?',
    openSettings: 'Open app settings',
  },
  sites: {
    actions: {
      fetchMany: {
        title: 'Featching sites',
        error: 'Failed to fetch sites',
      },
    },
    notFound: 'Site not found',
  },
  sideMenu: {
    home: 'Home',
    map: 'Map',
    vehicles: 'Vehicles',
    products: 'Products',
    packages: 'Packages',
    packaging: 'Packaging',
    packageTypes: 'Package types',
    styles: 'Styles',
    coils: 'Coils',
    systemsettings: 'System settings',
    dashboards: 'Dashboards',
    productionOrders: 'Production orders',
    reports: 'Reports',
    deliveries: 'Deliveries',
  },
  vehicleType(type: VehicleType): string {
    switch (type) {
      case VehicleType.Undefined:
        return 'Undefined';
      case VehicleType.Automatic:
        return 'Automatic';
      case VehicleType.LogHandler:
        return 'Log handler';
      case VehicleType.Truck:
        return 'Truck';
      case VehicleType.CoilForklift:
        return 'Coil forklift';
      case VehicleType.BulkHandler:
        return 'Bulk Handler';
      case VehicleType.PackageHandler:
        return 'Package handler';
    }
  },
  vehicleSymbol(symbol: VehicleSymbol): string {
    switch (symbol) {
      case VehicleSymbol.Undefined:
        return 'Arrow (Default)';
      case VehicleSymbol.LogStacker:
        return 'Log Stacker';
      case VehicleSymbol.WheelLoaderFrontGrapple:
        return 'Wheel Loader - Front Grapple';
      case VehicleSymbol.WheelLoaderHighGrapple:
        return 'Wheel Loader - High Grapple';
      case VehicleSymbol.TimberCrane:
        return 'Timber Crane';
      case VehicleSymbol.CoilForklift:
        return 'Coil Forklift';
    }
  },
  productType(type: ProductType): string {
    switch (type) {
      case ProductType.Log:
        return 'Log';
      case ProductType.Undefined:
        return 'Undefined';
      case ProductType.Coil:
        return 'Coil';
      case ProductType.Bulk:
        return 'Bulk';
      case ProductType.Dimension:
        return 'Dimension';
    }
  },
  products: {
    mapping: {
      matchCriteria: 'Match criteria',
      target: 'Target',
      created: 'Created',
      importRow: 'Import row',
      sourceProduct: 'Source product',
      sourceData: 'Source data',
      result: 'Result',
      correction: 'Correction',
    },
    actions: {
      filterByProduct: 'Filter by product',
      create: {
        title: 'Creating product',
        error: 'Failed to create product',
      },
      update: {
        title: 'Updating product',
        error: 'Failed to update product',
      },
      delete: {
        title: 'Deleting product',
        error: 'Failed to delete product',
        statusCodes: {
          409: 'Product is still in use in storage area',
        },
      },
      fetch: {
        title: 'Fetching product',
        error: 'Failed to fetch product',
      },
      fetchMany: {
        title: 'Fetching products',
        error: 'Failed to fetch products',
      },
      editLogYardProducts(number: number) {
        return `Edit ${number} logyard products`;
      },
      editCoilProducts(number: number) {
        return `Edit ${number} coil products`;
      },
      deleteMultipleProducts(number: number) {
        return `Delete ${number} products`;
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete product ${name}`;
      },
      description: 'Are you sure you want to delete this product? This action cannot be undone.',
      deleteButton: 'Yes delete product',
    },

    products: 'Products',
    addNewProduct: 'Add new product',
    editProduct: 'Edit product',
    editMultipleProducts(number: number) {
      return `Edit ${number} products`;
    },
    fetchDataError: 'Failed to fetch product data',
    addNewProductError: 'Could not create product',
    editProductError: 'Could not update product',
    deleteProductError: 'Could not delete product:',
    noProductsFound: 'No products found.',
    loadingProductData: 'Loading product data..',
    logsPerSquareMeter: 'logs/m²',
    notFound: 'Product not found',
    couldNotFetchProduct: 'Could not fetch product!',
    logData: {
      logData: 'Log data',
      logsPerSquareMeter: 'Logs per m²',
      minDiameter: 'Min diameter',
      maxDiameter: 'Max diameter',
      maxLength: 'Max length',
      minLength: 'Min length',
      averageCylinderVolume: 'Average cylinder volume',
      averagePhysicalVolume: 'Average physical volume',
      isSorted: 'Is sorted',
      species: 'Species',
      logSorterName: 'Log sorter name',
      sawLineName: 'Sawline name',
      logsPerSquareMeterDescription:
        'Used to estimate how many logs a vehicle can take from a storage area',
    },
    coilData: {
      title: 'Coil',
      diameter: 'Diameter',
      depth: 'Depth',
    },
    bulkData: {
      title: 'Bulk',
      unit: 'Unit',
      density: 'Density',
    },
    dimensionData: {
      title: 'Dimension',
      actualDimensions: 'Actual dimensions',
      nominalDimensions: 'Nominal dimensions',
      density: 'Density',
      thickness: 'Thickness',
      width: 'Width',
      length: 'Length',
      moisture: 'Moisture',
    },
    importDialog: {
      title: 'Import CSV',
      productType: 'Product type',
      selectType: 'Select a type',
      downloadTemplate: 'Download import template',
      choose: 'Choose',
      upload: 'Upload',
      uploadedFilePrefix: 'Uploaded file:',
      selectTypeBeforeFile: 'Please select a product type before choosing a file.',
      failedToParseCsvPrefix: 'Failed to parse CSV:',
      failedToParseCsvFile: 'Failed to parse CSV file.',
      missingRequiredColumns: 'Missing required column(s):',
      invalidRowsOf: 'of',
      invalidRowsHaveErrors: 'product(s) have validation errors. Hover the',
      hoverIconForCellDetails: 'icon on a cell for details.',
      allProductsValidPrefix: 'All',
      allProductsValidSuffix: 'product(s) look valid.',
      noDataLoaded: 'No data loaded yet. Choose a CSV file above.',
      status: 'Status',
      importAborted: 'Import aborted.',
      imported: 'Imported',
      productsWord: 'product(s).',
      summaryFailed: 'failed — hover the',
      hoverIconForStatusDetails: 'icon in the Status column for details.',
      close: 'Close',
      import: 'Import',
      abort: 'Abort',
      columnProductName: 'Product name',
      columnDescription: 'Description',
      columnExternalCode: 'External code',
      columnColor: 'Color',
      columnQuality: 'Quality',
      columnSpecies: 'Species',
      columnLogsPerSquareMeter: 'Logs/m²',
      columnMinDiameter: 'Min diameter',
      columnMaxDiameter: 'Max diameter',
      columnMaxLength: 'Max length',
      columnMinLength: 'Min length',
      columnAverageCylinderVolume: 'Avg cylinder vol.',
      columnAveragePhysicalVolume: 'Avg physical vol.',
      columnSorted: 'Sorted',
      columnLogSorter: 'Log sorter',
      columnSawLine: 'Saw line',
      columnDiameter: 'Diameter',
      columnDepth: 'Depth',
      columnDensity: 'Density',
      columnUnit: 'Unit',
      validationRequired: 'is required.',
      validationMustBeNumber: 'must be a number.',
      validationMustBeInteger: 'must be an integer.',
      validationMustBeBoolean: 'must be TRUE or FALSE.',
      validationMustBeHexColor: 'must be a hex color (e.g. #4d8aeb).',
      validationMustBeOneOf: 'must be one of:',
      validationMustBeAtLeast: 'must be at least:',
      duplicateProductNameInFile: 'Duplicate product name within this file.',
      productNameAlreadyExists: 'A product with this name already exists.',
      importFailedFallback: 'Import failed.',
    },
    deleteMultipleDialog: {
      deleteProducts: 'Delete products',
      areYouSure: 'Are you sure you want to delete these products?',
      thisActionCannotBeUndone: 'This action cannot be undone.',
      deleted: 'Deleted',
      products: 'products',
      summaryFailed: 'failed — hover the',
      hoverIconForDetails: 'icon in the Status column for details.',
      deleteAborted: 'The delete operation was aborted.',
      type: 'Type',
      DELETE: 'DELETE',
      toConfirm: 'to confirm',
      noProductsSelected: 'No products selected',
      deleteFailed: 'Delete failed',
    },
  },
  productGroups: {
    actions: {
      create: {
        title: 'Creating product group',
        error: 'Failed to create product group',
      },
      update: {
        title: 'Updating product group',
        error: 'Failed to update product group',
      },
      delete: {
        title: 'Deleting product group',
        error: 'Failed to delete product group',
      },
      fetch: {
        title: 'Fetching product group',
        error: 'Failed to fetch product group',
      },
      fetchMany: {
        title: 'Fetching product groups',
        error: 'Failed to fetch product groups',
      },
    },
    listPage: {
      title: 'Product groups',
      empty: 'No product groups',
      loading: 'Loading product groups...',
      addNew: 'Add new group',
      edit: 'Edit group',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete product group ${name}`;
      },
      description:
        'Are you sure you want to delete this product group? This action cannot be undone.',
      deleteButton: 'Yes delete product group',
    },
  },
  vehicles: {
    panels: {
      identity: 'Identity',
      preview: 'Preview',
      type: 'Type',
      movement: 'Movement',
      automatic: 'Automatic',
      keybindings: 'Keybindings',
    },
    actions: {
      create: {
        title: 'Creating vehicle',
        error: 'Failed to create vehicle',
      },
      update: {
        title: 'Updating vehicle',
        error: 'Failed to update vehicle',
      },
      delete: {
        title: 'Deleting vehicle',
        error: 'Failed to delete vehicle',
      },
      fetch: {
        title: 'Fetching vehicle',
        error: 'Failed to fetch vehicle',
      },
      fetchMany: {
        title: 'Fetching vehicles',
        error: 'Failed to fetch vehicles',
      },
      startReservation: {
        title: 'Starting vehicle reservation',
        error: 'Failed to reserve vehicle',
      },
      endReservation: {
        title: 'Ending vehicle reservation',
        error: 'Failed to end vehicle reservation',
      },
      updateKeybinds: {
        title: 'Updating vehicle keybinds',
        error: 'Failed to vehicle keybinds',
      },
    },
    vehicles: 'Vehicles',
    noVehiclesFound: 'No vehicles found.',
    loadingVehicleData: 'Loading vehicle data..',
    addNewVehicle: 'Add new vehicle',
    editVehicle: 'Edit vehicle',
    confirmDeleteMsg: 'Are you sure you want to delete this vehicle? This action cannot be undone.',
    deleteBtnTxt: 'Yes delete vehicle',
    fetchDataError: 'Failed to fetch vehicle data',
    editVehicleSuccess: 'Edited',
    addNewVehicleSuccess: 'Success: added',
    deleteVehicleSuccess: 'Success: deleted',
    deleteVehicleError: 'Could not delete vehicle:',
    nameMustBeUnique: 'Name must be unique',
    symbol: 'Symbol',
    symbolPreview: 'Preview',
    anchorPoint: 'Anchor point',
    anchorRotationOffset: 'Rotation offset',
    anchorRotationOffsetDescription:
      'Clockwise rotation applied to the vehicle icon and to the proximity direction, relative to the GPS heading. Leave at 0 for no offset.',
    usePickupPoint: 'Separate pickup point',
    usePickupPointDescription:
      'When enabled, proximity and area checks for logs or coils start from a separate pickup point on the icon instead of the anchor point.',
    pickupPoint: 'Pickup point',
    capacityDescription:
      'Used to estimate how many logs that this vehicle can take from a storage area. It will be used in combination with the products "logs per square meter".',
    packageCapacity: 'Package capacity',
    packageCapacityDescription: 'Maximum number of packages this vehicle can carry at once.',
    proximityRange: 'Proximity range',
    proximityRangeDescription: 'Detection range for package pickup and putdown.',
    proximityConeAngle: 'Proximity cone',
    proximityConeAngleDescription:
      'Access cone half-angle for package pickup and putdown. The vehicle must approach a place from within this angle.',
    headingSmoothingTime: 'Heading smoothing',
    headingSmoothingTimeDescription:
      'Time constant for smoothing the vehicle heading to reduce GPS jitter. Higher values are smoother but slower to react. Leave empty for no smoothing (the heading follows GPS directly).',
    stationarySpeedThreshold: 'Stationary speed threshold',
    stationarySpeedThresholdDescription:
      'Below this speed the vehicle is treated as stationary and its heading is held steady instead of following unreliable GPS heading. Leave empty for the default of 0.5 km/h. Has no effect when neutral turn is enabled.',
    neutralTurn: 'Neutral turn',
    neutralTurnDescription: 'The vehicle can turn on the spot.',
    reverseDetection: 'Reverse detection',
    reverseDetectionDescription: 'Detect when the vehicle reverses and adjust its direction accordingly.',
    packagePickupDirection: 'Package pickup direction',
    packagePickupDirectionDescription:
      'From which side of a package this vehicle picks up and sets packages down.',
    packagePickupDirectionAny: 'Any side',
    packagePickupDirectionLengthSide: 'Length side (left/right)',
    packagePickupDirectionWidthSide: 'Width side (ends)',
    placementPreview: 'Placement preview',
    placementPreviewDescription:
      'Show a semi-transparent ghost of planned package positions before putting them down.',
    deleteVehicle(name: string) {
      return `Delete vehicle ${name}`;
    },
    inUseDialog: {
      header: 'Vehicle is in use!',
    },
    reservationEnded: 'Someone else took over your vehicle!',
    reservationEndedDescription: (userId: string) =>
      `Your vehicle was taken by ${userId.split('|')[0]}`,
    automaticLogYardMoves: 'Automatic log yard moves',
    automaticLogYardMovesLogSorterBinFull: 'When log sorter bin is full',
    automaticLogYardMovesLogSorterBinChangeProduct: 'When log sorter bin change product',
  },
  addLogSorterDialog: {
    addLogSorter: 'Add log sorter',
    editLogSorter: 'Edit log sorter',
    name: 'Name',
    bin: 'Bin',
    bins: 'Bins',
    identifiers: 'Id',
    logSorter: 'Log sorter',
    selectLogSorter: 'Select log sorter',
    startPositionX: 'Start position X',
    startPositionY: 'Start position Y',
    columns: 'Columns',
    numberOfBins: 'Number of bins',
    numberSequence: 'Number sequence',
    rotation: 'Rotation',
    direction: 'Direction',
    binWidth: 'Bin width',
    binLength: 'Bin height',
    distanceX: 'Distance between X',
    distanceY: 'Distance between Y',
    geoFenceOffsetTop: 'Geofence top',
    geoFenceOffsetBottom: 'Geofence bottom',
    geoFenceOffsetLeft: 'Geofence left',
    geoFenceOffsetRight: 'Geofence right',
    geoFenceOffsetInside: 'Geofence inside',
    geoFenceOffsetOutside: 'Geofence outside',
    addLogWhenSorted: 'Add log when sorted',
    intake: 'Intake',
    intakes: 'Intakes',
    addNewIntake: 'Add new intake',
    deleteIntake: 'Delete intake',
    savingStorageAreas: 'Saving storage areas',
    savingShapes: 'Saving shapes',
    savingLogSorter: 'Saving log sorter',
    success: 'Success!',
    savingBins: 'Saving bins',
    savingIntakes: 'Saving intakes',
    one: '1',
    twoLeft: '2L',
    twoRight: '2R',
  },
  dialogs: {
    storageArea: {
      addStorageArea: 'Create storage area',
      editStorageArea: 'Edit storage area',
      storageArea: 'Storage area',
      automaticProductChange: 'Automatic product change',
      allowMultipleProducts: 'Allow multiple products',
      allowedProducts: 'Allowed products',
      selectAllowedProducts: 'Select allowed products',
      selectStorageAreaType: 'Select storage area type',
      selectStorageAreaStatus: 'Select storage area status',
      confirmDeleteHeader: 'Delete storage area',
      confirmDeleteMessage: (name: string) =>
        `Are you sure you want to delete the storage area: ${name}?`,
      deleteError: 'Could not delete storage area',
      updateError: 'Could not update storage area',
      createError: 'Could not create storage area',
      editShape: 'Edit shape',
      sideArea: 'Side area',
      sideAreaDescription: 'Used to estimate the amount of logs that fits inside this storage area',
    },
    storageAreaSelect: {
      title: 'Storage area info',
    },
    inventory: {
      title: 'Inventory',
      selectProduct: 'Select product',
      currentAmount: 'Current amount:',
      setNewAmount: 'Set new amount',
      storageAreaNotFound: 'Storage area not found!',
      confirmHeader: (name: string) => `Inventory ${name}`,
      confirmMessage: (name: string, product: string) =>
        `Are you sure you want to add ${product} to ${name}? Allow multiple products is disabled.`,
    },
    confirmEmpty: {
      message:
        'There is still content in the storage area. Are you sure you wanna empty it? Remaining content will be deleted.',
      title: (name: string) => `Empty storage area ${name}`,
      willBeDeleted: 'will be deleted',
    },
    confirmAllowMultipleProducts: {
      message: (storageAreaName: string, product: string) =>
        `Allowed multiple products is disabled. Are you sure you wanna place "${product}" in ${storageAreaName}`,
      title: 'Warning',
    },
    confirmPlaceNotAllowedProductInLogYard: {
      message: (storageAreaName: string, products: string) =>
        `The product "${products}" are not allowed in ${storageAreaName}. Are you sure you want to place it here?`,
      title: 'Warning',
    },
    moveLogYardContent: {
      pickTab: 'Pick from',
      pickButton: 'Pick',
      placeTab: 'Place in',
      placeButton: 'Place',
      pickAll: 'Empty',
    },
    selectVehicle: {
      title: 'Select vehicle',
      message: 'Please select the vehicle you are using',
    },
    vehicleInventory: {
      vehicleInventory: 'Vehicle inventory',
      vehicleLoad: 'Current vehicle load',
      leaveTitle: 'Leave in',
      leaveButton: 'Leave',
      moving: 'Moving',
    },
    search: {
      searchForProduct: 'Search for a product',
      selectAProduct: 'Select a product',
    },
    mapView: {
      title: 'Add new map view',
      enterName: 'Enter a name',
    },
    selectLogSorter: {
      title: 'Select log sorter',
    },
    productionOrder: {
      editTitle: 'Select production order',
      selectProductionOrder: 'Select production order',
    },
  },
  error: {
    required: 'Required.',
    invalidValue: 'Invalid value.',
    nameAlreadyExist: 'Name already exist.',
    nameIsRequired: 'A name is required.',
    maxLength: (limit: number) => `Max length ${limit}`,
    forbidden: 'Forbidden',
    notFound: 'Not found',
    conflict: 'Conflict',
    network: 'Could not reach server',
    invalidDensity: 'Density needs to be atleast 0.1',
  },
  enums: {
    storageAreaStatus(status: StorageAreaStatus): string {
      switch (status) {
        case StorageAreaStatus.Empty:
          return 'Empty';
        case StorageAreaStatus.InUse:
          return 'In use';
        case StorageAreaStatus.Full:
          return 'Full';
        case StorageAreaStatus.Undefined:
          return 'Undefined';
      }
    },
    storageAreaType(type: StorageAreaType): string {
      switch (type) {
        case StorageAreaType.Producer:
          return 'Producer';
        case StorageAreaType.Consumer:
          return 'Consumer';
        case StorageAreaType.Vehicle:
          return 'Vehicle';
        case StorageAreaType.LogSorterIntake:
          return 'Logsorter intake';
        case StorageAreaType.SawLineIntake:
          return 'Sawline intake';
        case StorageAreaType.LogSorterBin:
          return 'Logsorter bin';
        case StorageAreaType.SortedPile:
          return 'Sorted pile';
        case StorageAreaType.UnsortedPile:
          return 'Unsorted pile';
        case StorageAreaType.Truck:
          return 'Truck';
        case StorageAreaType.Train:
          return 'Train';
        case StorageAreaType.Undefined:
          return 'Undefined';
        case StorageAreaType.CoilStack:
          return 'Coil stack';
        case StorageAreaType.Bulk:
          return 'Bulk';
        case StorageAreaType.PackageStorage:
          return 'Package storage';
        case StorageAreaType.ProductionLine:
          return 'Production line';
        case StorageAreaType.PackageOutput:
          return 'Package output';
        case StorageAreaType.PackageIntake:
          return 'Package intake';
      }
    },
    shapeMode(type: ShapeMode): string {
      switch (type) {
        case ShapeMode.Rectangle:
          return 'Rectangle';
        case ShapeMode.Circle:
          return 'Circle';
        case ShapeMode.Polygon:
          return 'Polygon';
      }
    },
    logSorterNumberSequence(value: LogSorterNumberSequence): string {
      switch (value) {
        case LogSorterNumberSequence.Sequential:
          return 'Sequential';
        case LogSorterNumberSequence.SequentialMirrored:
          return 'Sequential mirrored';
        case LogSorterNumberSequence.Alternating:
          return 'Alternating';
        case LogSorterNumberSequence.AlternatingMirrored:
          return 'Alternating mirrored';
      }
    },
    logSorterBinLayout(value: LogSorterColumnPlacement): string {
      switch (value) {
        case LogSorterColumnPlacement.Left:
          return 'Left';
        case LogSorterColumnPlacement.Right:
          return 'Right';
      }
    },
    anchor(value: Anchor): string {
      switch (value) {
        case Anchor.Center:
          return 'Center';
        case Anchor.BottomCenter:
          return 'Bottom Center';
        case Anchor.BottomLeft:
          return 'Bottom Left';
        case Anchor.BottomRight:
          return 'Bottom Right';
        case Anchor.TopCenter:
          return 'Top Center';
        case Anchor.TopLeft:
          return 'Top Left';
        case Anchor.TopRight:
          return 'Top Right';
      }
    },
    auditTrailAction(action: AuditTrailAction): string {
      switch (action) {
        case AuditTrailAction.InventoryApiExecute:
          return 'Inventory';
        case AuditTrailAction.InventoryApiUndo:
          return 'Undo inventory';
        case AuditTrailAction.LogSorterApiCreate:
          return 'Create log sorter';
        case AuditTrailAction.LogSorterApiDelete:
          return 'Delete log sorter';
        case AuditTrailAction.LogSorterApiUpdate:
          return 'Update log sorter';
        case AuditTrailAction.MapStyleApiCreate:
          return 'Create map style';
        case AuditTrailAction.MapStyleApiDelete:
          return 'Delete map style';
        case AuditTrailAction.MapStyleApiUpdate:
          return 'Update map style';
        case AuditTrailAction.MoveApiUndo:
          return 'Undo move';
        case AuditTrailAction.MoveApi:
          return 'Move';
        case AuditTrailAction.ProductApiCreate:
          return 'Create product';
        case AuditTrailAction.ProductApiDelete:
          return 'Delete product';
        case AuditTrailAction.ProductApiUpdate:
          return 'Update product';
        case AuditTrailAction.PackageApiCreate:
          return 'Create package';
        case AuditTrailAction.PackageApiUpdate:
          return 'Update package';
        case AuditTrailAction.PackageApiDelete:
          return 'Delete package';
        case AuditTrailAction.PackagingApiCreate:
          return 'Create packaging';
        case AuditTrailAction.PackagingApiUpdate:
          return 'Update packaging';
        case AuditTrailAction.PackagingApiDelete:
          return 'Delete packaging';
        case AuditTrailAction.PackageTypeApiCreate:
          return 'Create package type';
        case AuditTrailAction.PackageTypeApiUpdate:
          return 'Update package type';
        case AuditTrailAction.PackageTypeApiDelete:
          return 'Delete package type';
        case AuditTrailAction.SyncPackage:
          return 'Package update';
        case AuditTrailAction.ShapeApiCreate:
          return 'Create shape';
        case AuditTrailAction.ShapeApiDelete:
          return 'Delete shape';
        case AuditTrailAction.ShapeApiUpdate:
          return 'Update shape';
        case AuditTrailAction.ShapeApiUndoDelete:
          return 'Undo update shape';
        case AuditTrailAction.StorageAreaApiCreate:
          return 'Create storage area';
        case AuditTrailAction.StorageAreaApiDelete:
          return 'Delete storage area';
        case AuditTrailAction.StorageAreaApiUndoDelete:
          return 'Undo delete storage area';
        case AuditTrailAction.StorageAreaApiUpdate:
          return 'Update storage area';
        case AuditTrailAction.SyncMove:
          return 'Move';
        case AuditTrailAction.VehicleApiCreate:
          return 'Create vehicle';
        case AuditTrailAction.VehicleApiDelete:
          return 'Delete vehicle';
        case AuditTrailAction.VehicleApiUpdate:
          return 'Update vehicle';
        case AuditTrailAction.Undefined:
          return 'Undefined';
        case AuditTrailAction.SyncInventory:
          return 'Sync inventory';
        case AuditTrailAction.SyncProduct:
          return 'Sync product';
        case AuditTrailAction.SyncShape:
          return 'Sync shape';
        case AuditTrailAction.SyncStorageArea:
          return 'Sync storage area';
        case AuditTrailAction.SyncUndoInventory:
          return 'Sync undo inventory';
        case AuditTrailAction.SyncUndoMove:
          return 'Sync undo move';
        case AuditTrailAction.SyncUndoStorageArea:
          return 'Synt undo storage area';
        case AuditTrailAction.CoilApiCreate:
          return 'Create coil';
        case AuditTrailAction.CoilApiUpdate:
          return 'Update coil';
        case AuditTrailAction.CoilApiDelete:
          return 'Delete coil';
        case AuditTrailAction.FeatureFlagApi:
          return 'Update Feature flags';
        case AuditTrailAction.SettingUpdate:
          return 'Update settings';
        case AuditTrailAction.SyncFeatureFlag:
          return 'Sync feature flags';
        case AuditTrailAction.SyncLogSorter:
          return 'Sync log sorter';
        case AuditTrailAction.SyncMapStyle:
          return 'Sync map styles';
        case AuditTrailAction.SyncSetting:
          return 'Sync settings';
        case AuditTrailAction.SyncVehicle:
          return 'Sync vehicles';
        case AuditTrailAction.ProductGroupApiCreate:
          return 'Create product group';
        case AuditTrailAction.ProductGroupApiUpdate:
          return 'Update product group';
        case AuditTrailAction.ProductGroupApiDelete:
          return 'Delete product group';
        case AuditTrailAction.ProductGroupSync:
          return 'Sync product group';
        case AuditTrailAction.DashboardCreate:
          return 'Create dashboard';
        case AuditTrailAction.DashboardUpdate:
          return 'Update dashboard';
        case AuditTrailAction.DashboardDelete:
          return 'Delete dashboard';
        case AuditTrailAction.MoveTimberFromLogSorterBins:
          return 'Move timber from log sorter bins';
        case AuditTrailAction.ProductionOrderCreate:
          return 'Create production order';
        case AuditTrailAction.ProductionOrderUpdate:
          return 'Update production order';
        case AuditTrailAction.ProductionOrderDelete:
          return 'Delete production order';
        case AuditTrailAction.ProductionOrderSync:
          return 'Sync production order';
        case AuditTrailAction.ProductionOrderChange:
          return 'Change production order';
        case AuditTrailAction.InboundDeliveryCreate:
          return 'Create inbound delivery';
        case AuditTrailAction.InboundLoadCreate:
          return 'Create inbound load';
        case AuditTrailAction.SyncInboundLoad:
          return 'Sync inbound load';
        case AuditTrailAction.SyncInboundDelivery:
          return 'Sync inbound delivery';
        case AuditTrailAction.InboundDeliveryDelete:
          return 'Delete inbound delivery';
        case AuditTrailAction.InboundDeliveryUpdate:
          return 'Update inbound delivery';
        case AuditTrailAction.OutboundDeliveryCreate:
          return 'Create outbound delivery';
        case AuditTrailAction.OutboundDeliveryUpdate:
          return 'Update outbound delivery';
        case AuditTrailAction.OutboundDeliveryDelete:
          return 'Delete outbound delivery';
        case AuditTrailAction.SyncOutboundDelivery:
          return 'Sync outbound delivery';
        case AuditTrailAction.InboundDeliveryTransaction:
          return 'Inbound delivery transaction';
        case AuditTrailAction.OutboundDeliveryTransaction:
          return 'Outbound delivery transaction';
        case AuditTrailAction.SyncInboundDeliveryTransaction:
          return 'Sync inbound delivery transaction';
        case AuditTrailAction.SyncOutboundDeliveryTransaction:
          return 'Sync outbound delivery transaction';
        default:
          return 'Unknown action';
      }
    },
    dashboardComponentType(type: DashboardComponentType): string {
      switch (type) {
        case DashboardComponentType.StorageAreaTable:
          return 'Storage area table';
        case DashboardComponentType.LatestReleaseNotes:
          return 'Latest release notes';
        case DashboardComponentType.ProductGroupDiagram:
          return 'Product group diagram';
        case DashboardComponentType.ProductGroupTable:
          return 'Product group table';
        case DashboardComponentType.ProductTable:
          return 'Product table';
        case DashboardComponentType.AsljungapallenLagersaldo:
          return 'Åsljungapallen lagersaldo';
        default:
          return 'Unknown dashboardcomponenttype';
      }
    },
    keyboardAction(action: KeyboardActions): string {
      switch (action) {
        case KeyboardActions.DropOff:
          return 'Drop off';
        case KeyboardActions.PickUp:
          return 'Pick up';
        case KeyboardActions.ZoomIn:
          return 'Zoom in';
        case KeyboardActions.ZoomOut:
          return 'Zoom out';
        case KeyboardActions.ChangeMapStyle:
          return 'Change map style';
        case KeyboardActions.OpenInventory:
          return 'Open/Close inventory';
        case KeyboardActions.RotateLeft:
          return 'Rotate left';
        case KeyboardActions.RotateRight:
          return 'Rotate right';
        case KeyboardActions.ToggleStorageAreaFull:
          return 'Toggle storagearea to full / in use';
        case KeyboardActions.OpenStorageArea:
          return 'Open/Close storage area';
        case KeyboardActions.Undo:
          return 'Undo';
        case KeyboardActions.CreateStorageArea:
          return 'Create storage area';
        case KeyboardActions.SelectNextNearbyStorageArea:
          return 'Select next nearby storage area';
        case KeyboardActions.SelectPreviousNearbyStorageArea:
          return 'Select previous nearby storage area';
      }
    },
    productionOrderState(state: ProductionOrderState): string {
      switch (state) {
        case ProductionOrderState.Deleted:
          return 'Deleted';
        case ProductionOrderState.Planned:
          return 'Planned';
        case ProductionOrderState.Released:
          return 'Released';
        case ProductionOrderState.Reserved:
          return 'Reserved';
        case ProductionOrderState.Preparing:
          return 'Preparing';
        case ProductionOrderState.Prepared:
          return 'Prepared';
        case ProductionOrderState.Delivering:
          return 'Delivering';
        case ProductionOrderState.Completed:
          return 'Completed';
        case ProductionOrderState.Registered:
          return 'Registered';
        case ProductionOrderState.PreparingPaused:
          return 'Preparing paused';
        case ProductionOrderState.DeliveringPaused:
          return 'Delivering paused';
      }

      return 'Unknown';
    },
    deliveryStatus(status: DeliveryStatus): string {
      switch (status) {
        case DeliveryStatus.Planned:
          return 'Planned';
        case DeliveryStatus.Released:
          return 'Released';
        case DeliveryStatus.Active:
          return 'Active';
        case DeliveryStatus.Finished:
          return 'Finished';
        case DeliveryStatus.Invoiced:
          return 'Invoiced';
      }

      return 'Unknown';
    },
  },
  reports: {
    exportFormat: {
      pdf: 'PDF',
      excel: 'Excel',
    },
    reportName(report: ReportType): string {
      switch (report) {
        case ReportType.LogYardStatus:
          return 'Status report';
        case ReportType.LogYardPeriod:
          return 'Period report';
        case ReportType.LogYardVehicleMoves:
          return 'Vehicle moves';
        case ReportType.LogYardMovedProducts:
          return 'Moved products';
        case ReportType.LogYardVehicleStatistics:
          return 'Vehicle statistics';
        default:
          return 'Unknown report';
      }
    },
  },
  storageAreas: {
    actions: {
      create: {
        title: 'Create storage area',
        pending: 'Creating storage area',
        error: 'Failed to create storage area',
      },
      fetch: {
        title: 'Fetching storage area',
        error: 'Failed to fetch storage area',
      },
      fetchMany: {
        title: 'Fetching storage areas',
        error: 'Failed to fetch storage areas',
      },
      inventory: {
        title: 'Inventory storage area',
        error: 'Failed to inventory storage area',
      },
      undoInventory: {
        title: 'Undo inventory',
        error: 'Failed to undo inventory',
      },
      delete: {
        title: 'Deleting storage area',
        error: 'Failed to delete storage area',
      },
      update: {
        title: 'Updating storage area',
        error: 'Failed to update storage area',
      },
      undoDelete: {
        title: 'Undo delete storage area',
        error: 'Failed to undo delete storage area',
      },
    },
    copy: 'Copy',
    notFound: 'Storage area not found',
    storageAreaTable: {
      firstMoveTo: 'First move',
      daysSinceFirstMove: 'Days since first move',
    },
  },
  productionOrders: {
    actions: {
      fetchMany: {
        title: 'Fetching production orders',
        error: 'Failed to fetch production orders',
      },
      fetch: {
        title: 'Fetching production order',
        error: 'Failed to fetch production order',
      },
      create: {
        title: 'Creating production order',
        error: 'Failed to create production order',
      },
      update: {
        title: 'Updating production order',
        error: 'Failed to update production order',
      },
      delete: {
        title: 'Deleting production order',
        error: 'Failed to delete production order',
        statusCodes: {
          409: 'Failed to delete production order',
        },
      },
      change: {
        title: 'Changing production order',
        error: 'Failed to change production order',
      },
    },
    listPage: {
      title: 'Production orders',
      noFound: 'No production order found',
      loading: 'Loading production orders...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete production order ${name}`;
      },
      description:
        'Are you sure you want to delete this production order? This action cannot be undone.',
      deleteButton: 'Yes delete production order',
    },
    editPage: {
      editTitle: 'Edit production order',
      createTitle: 'Create production order',
      productionInstruction: 'Production instruction',
      vehicleInstruction: 'Vehicle instruction',
      state: 'State',
      recipeInstruction: 'Instructions',
      recipeMachineInstruction: 'Machine instructions',
      consumeEntireStock: 'Consume entire stock',
      noProducts: 'No products',
      inProductsTitle: 'In products',
      outProductsTitle: 'Out products',
      editRecipeTitle: 'Edit recipe',
      addRecipeTitle: 'Add recipe',
      addRecipeButton: 'Add recipe',
      recipesTitle: 'Recipes',
    },
    plannedStart: 'Planned start',
  },
  shapes: {
    name: (id: string) => `Shape #${id}`,
    multipleNames: (number: number) => `Edit (${number}) shapes`,
    actions: {
      fetchMany: {
        title: 'Fetching shapes',
        error: 'Failed to fetch shapes',
      },
      fetch: {
        title: 'Fetching shape',
        error: 'Failed to fetch shape',
      },
      update: {
        title: 'Updating shape',
        error: 'Failed to update shape',
      },
      create: {
        title: 'Create shape',
        pending: 'Creating shape',
        error: 'Failed to create shape',
      },
      delete: {
        title: 'Deleting shape',
        error: 'Failed to delete shape',
      },
      query: {
        title: 'Querying shapes',
        error: 'Failed to query shapes',
      },
      undoDelete: {
        title: 'Undo delete shape',
        error: 'Failed to undo delete shape',
      },
    },
    geofenceFieldSet: 'Geofence',
    zIndex: 'Z-index',
  },
  settings: {
    actions: {
      read: {
        title: 'Read settings',
        error: 'Failed to read settings',
      },
      save: {
        title: 'Save settings',
        error: 'Failed to save settings',
      },
    },
    general: {
      title: 'General',
      language: 'Language',
      dark: 'Dark',
      light: 'Light',
      colorMode: 'Color mode',
      scale: 'Size of user interface',
      auto: 'Auto',
      mapColorMode: 'Map color mode',
    },
    map: {
      title: 'Map',
      style: 'Style',
      coordinate: 'Coordinates',
      centerMapOnVehicle: 'Center map on vehicle',
      followVehicleHeading: 'Rotate map with the vehicle',
      snapPlacementToPlace: 'Snap put-down package to storage place',
      showPackagePanel: 'Show package panel',
    },
    keybinds: {
      title: 'Keybinds',
    },
    about: {
      title: 'About',
    },
    vehicle: {
      title: 'Vehicle',
      activeVehicle: 'Active vehicle',
      speedLimit: 'Speed limit 5 km/h to trigger storage area',
    },
  },
  featureFlags: {
    actions: {
      read: {
        title: 'Read feature flags',
        error: 'Failed to read feature flags',
      },
      refresh: {
        title: 'Refresh feature flags',
        error: 'Failed to refresh feature flags',
      },
      toggle: {
        title: 'Toggle feature flags',
        error: 'Failed to toggle feature flags',
      },
    },
    refresh: {
      success: 'Feature flags refreshed successfully',
    },
    toggle: {
      success: 'Feature flag toggled successfully',
    },
    component: {
      title: 'Feature flags',
      description: 'Enable or disable features in the application',
      refreshButton: 'Refresh feature flags',
    },
  },
  auth: {
    loggedOut: {
      title: 'You are logged out!',
      description: 'You need to login to be able to continue working.',
    },
    unauthorized: {
      title: 'Your user is not allowed to use this app',
      description: 'You need to talk to your administrator to give you the right to use it.',
    },
  },
  map: {
    contextmenu: {
      create: {
        title: 'Create',
        shape: 'Shape',
        storageArea: 'Storage area',
        logSorter: 'Log sorter',
        intake: 'Intake',
        productionLine: 'Production line',
        insideProductionLine: 'Create inside production line',
        packageOutput: 'Package output',
        packageIntake: 'Package intake',
      },
      edit: 'Edit',
      delete: 'Delete',
      audit: 'Audit',
      copy: 'Copy',
      move: 'Move',
      addInboundDeliveryTransaction: 'Add inbound transaction',
      addOutboundDeliveryTransaction: 'Add outbound transaction',
      spawnPackage: 'Spawn package',
      copyPackage: 'Copy package',
    },
    actions: {
      saveMapMode: {
        title: 'Save map mode',
        error: 'Failed to save map mode',
      },
    },
  },
  productionLine: {
    simulation: 'Simulation',
    areas: 'Areas',
    addOutputArea: 'Add output area',
    addIntakeArea: 'Add intake area',
    saveLineFirst: 'Save the production line before adding output or intake areas.',
    simulationEnabled: 'Simulation enabled',
    intervalSeconds: 'Interval (seconds)',
    outputPlace: 'Output place',
    product: 'Product',
    piecesCount: 'Pieces count',
    packaging: 'Packaging',
    packageType: 'Package type',
    length: 'Length',
    width: 'Width',
    height: 'Height',
  },
  logSorter: {
    actions: {
      query: {
        title: 'Querying log sorter',
        error: 'Failed to query log sorter',
      },
      update: {
        title: 'Updating log sorter',
        error: 'Failed to update log sorter',
      },
      create: {
        title: 'Creating log sorter',
        error: 'Failed to create log sorter',
      },
      delete: {
        title: 'Deleting log sorter',
        error: 'Failed to delete log sorter',
      },
      fetch: {
        title: 'Fetching log sorter',
        error: 'Failed to fetch log sorter',
      },
    },
    drawer: {
      createTitle: 'Create log sorter',
      binName: (binNumber: number) => `Bin ${binNumber}`,
      logSorterTab: 'Log sorter',
      binsTab: 'Bins',
    },
    deleteConfirm: {
      title: `Delete log sorter`,
      message: (name: string) => `Are you sure you want to delete the log sorter ${name}?`,
    },
    panel: {
      identifier: 'Identifier',
      numberSequence: 'Number sequence',
      numberOfBinsCol1: 'Number of bins in column one',
      numberOfBinsCol2: 'Number of bins in column two',
      binLayout: 'Column two to',
      binWidth: 'Bin width',
      binLength: 'Bin length',
      columnGap: 'Column gap',
      rowGap: 'Row gap',
      intakes: 'Intakes',
      offsetY: 'Offset Y',
      offsetX: 'Offset X',
    },
  },
  mapStyles: {
    actions: {
      create: {
        title: 'Creating style',
        error: 'Failed to create style',
      },
      update: {
        title: 'Updating style',
        error: 'Failed to update style',
      },
      delete: {
        title: 'Deleting style',
        error: 'Failed to delete style',
      },
      fetch: {
        title: 'Fetching style',
        error: 'Failed to fetch style',
      },
      fetchMany: {
        title: 'Fetching styles',
        error: 'Failed to fetch styles',
      },
    },

    deleteDialog: {
      header(name: string) {
        return `Delete style ${name}`;
      },
      description: 'Are you sure you want to delete this style? This action cannot be undone.',
      deleteButton: 'Yes delete style',
    },

    editDialog: {
      create: {
        title: 'Create new style',
      },
      edit: {
        title: 'Edit style',
      },
      tip: 'You can press ctrl+space to open the show info about avaliable functions',
      documentation: 'For more information about the formulas, see the ',
      documentationLinkText: 'map style documentation',
      shapeSet: 'Shape',
      vehicleSet: 'Vehicle',
      labelFormula: 'Label',
      colorFormula: 'Color',
      outlineColorFormula: 'Outline color',
      outlineSizeFormula: 'Outline size',
      fontSizeFormula: 'Font size',
      messageIconFormula: 'Show message icon',
      placeSet: 'Storage places',
      placeVisibleFormula: 'Visible',
      placeLabelFormula: 'Label',
      placeOutlineColorFormula: 'Outline color',
      placeOutlineSizeFormula: 'Outline size',
      placeAccessibleOutlineColorFormula: 'Accessible side outline color',
      placeInaccessibleOutlineColorFormula: 'Inaccessible side outline color',
      placeTextAlignmentFormula: 'Text alignment (left, center, right)',
      placeTextPositionFormula: 'Text position (center, top, bottom)',
      placeTextOpacityFormula: 'Text opacity (0-100)',
      placeTextColorFormula: 'Text color',
      placeTextSizeFormula: 'Text size',
      placeBackgroundColorFormula: 'Background color',
      placeBackgroundPatternFormula: 'Background pattern (solid, hatch, cross, dots)',
      placeBackgroundOpacityFormula: 'Background opacity (0-100)',
      packageSet: 'Packages',
      packageVisibleFormula: 'Visible',
      packageLabelFormula: 'Label',
      packageOutlineColorFormula: 'Outline color',
      packageOutlineSizeFormula: 'Outline size',
      packageTextAlignmentFormula: 'Text alignment (left, center, right)',
      packageTextPositionFormula: 'Text position (center, top, bottom)',
      packageTextOpacityFormula: 'Text opacity (0-100)',
      packageTextColorFormula: 'Text color',
      packageTextSizeFormula: 'Text size',
      packageBackgroundColorFormula: 'Background color',
      packageBackgroundPatternFormula: 'Background pattern (solid, hatch, cross, dots)',
      packageBackgroundOpacityFormula: 'Background opacity (0-100)',
      proximitySet: 'Proximity',
      proximityVisibleFormula: 'Visible',
      proximityAreaColorFormula: 'Area color',
      proximityConeColorFormula: 'Access cone color',
      colorPicker: 'Color Picker (Used to display and select color codes)',
    },

    page: {
      title: 'Map Styles',
      notFound: 'No map styles found',
      loading: 'Loading map styles',
      copiedToClipboard: 'Copied to clipboard!',
      noStyleInClipboard: 'No style in clipboard',
      noStyleInClipboardDescription:
        'You need to export a style into clipboard before you can import it.',
      importFromClipboardTooltip: 'Import from clipboard',
      failedToImport: 'Failed to import from clipboard:',
      clipBoardAccessDeniedSummary: 'Clipboard access denied',
      clipBoardAccessDeniedDetail: 'Please allow clipboard access for this site.'
    },
  },
  systemSettings: {
    page: {
      title: 'System Settings',
      settingsSaved: 'Settings saved successfully',
      storagearea: {
        title: 'Storage area',
        nameFormat: 'Name format',
        nameFormatRequired: 'Name format is required',
        nameFormatMissingSequence: 'Name format must contain placeholder {#} for sequence',
        displayText: 'Display text',
        displayTextFormula: 'Display text formula',
        displayTextFormulaTitle: 'Configure display text',
        displayTextFormulaDescription:
          'This formula is used to compute the display text of every storage area. It uses the same variables and functions as the map style shape formulas.',
        configureDisplayText: 'Configure display text formula',
        placeDisplayText: 'Place display text',
        placeDisplayTextFormula: 'Place display text formula',
        placeDisplayTextFormulaTitle: 'Configure place display text',
        placeDisplayTextFormulaDescription:
          'This formula is used to compute the display text of every storage place. It uses the same variables and functions as the map style place formulas.',
        configurePlaceDisplayText: 'Configure place display text formula',
        logYardLength: 'Log yard length',
        logYardWidth: 'Log yard width',
        logYardSideArea: 'Log yard side area',
        logYard: 'Log yard',
      },
      featureflags: {
        title: 'Feature Flags',
      },
      reports: {
        title: 'Reports',
        defaultReports: 'Default reports',
        customerSpecificReports: 'Only for specific customer',
      },
      map: {
        minZoom: 'The minimum zoom level of the map (0-24)',
      },
      dashboards: {
        title: 'Dashboardcomponents',
        defaultComponents: 'Default components',
        customerSpecificComponents: 'Only for specific customer',
      },
    },
  },
  actions: {
    fetchVersion: {
      title: 'Fetching application version',
      error: 'Failed to fetch application version',
    },
  },
  undo: {
    undoBtn: 'Undo',
    pickTitle: 'Undo pick action',
    getUndoPickDescription(
      oldStorageAreaCount: number,
      oldVehicleCount: number,
      storageAreaName: string,
    ): string {
      return `Confirm to remove ${oldVehicleCount} logs from your vehicle and restore storage area ${storageAreaName} to amount: ${oldStorageAreaCount}`;
    },
    getUndoPickBulkDescription(products: string, storageAreaName: string): string {
      return `Restore ${products} to ${storageAreaName} and remove it from your vehicle.`;
    },
    placeTitle: 'Undo place action',
    getUndoPlaceDescription(numberOfLogs: number, storageAreaName: string): string {
      return `Confirm to restore ${numberOfLogs} logs to your vehicle and remove ${numberOfLogs} logs from storage area ${storageAreaName}`;
    },
    getUndoPlaceBulkDescription(products: string, storageAreaName: string): string {
      return `Undo placing ${products} into ${storageAreaName}. The bulk material will be removed from the storage area and restored to your vehicle.`;
    },
    inventoryTitle: 'Undo inventory action',
    getUndoInventoryDescription(
      productname: string,
      numberOfLogs: number,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storageArea ${storageAreaName} and set amount of ${productname} back to ${numberOfLogs}`;
    },
    getUndoBulkInventoryDescription(
      productname: string,
      value: number,
      unit: Unit,
      storageAreaName: string,
    ): string {
      return `Confirm to undo the latest inventory action on storage area ${storageAreaName} and set amount of ${productname} back to ${value}`;
    },
    createStorageAreaTitle: 'Undo create storage area',
    getUndoCreateStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo create storage area ${storageAreaName}`;
    },
    deleteStorageAreaTitle: 'Undo delete storage area',
    getUndoDeleteStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo delete storage area ${storageAreaName}`;
    },
    editStorageAreaTitle: 'Undo edit storage area',
    getUndoEditStorageAreaDescription(storageAreaName: string): string {
      return `Confirm to undo edit on storage area ${storageAreaName}`;
    },
    createShape: {
      title: 'Undo create shape',
      description: `Confirm to undo create shape`,
    },
    updateShape: {
      title: 'Undo update shape',
      description: 'Confirm to undo update shape',
    },
    deleteShape: {
      title: 'Undo delete shape',
      description: 'Confirm to undo delete shape',
    },
  },
  quickMenu: {
    addStorageArea: 'Create storage area',
    editStorageArea: 'Edit storage area',
  },
  moves: {
    actions: {
      moveTimberFromLogSorterBins: {
        title: 'Move timber from log sorter bins',
        error: 'Failed to move timber from log sorter bins',
      },
      pick: {
        title: 'Picking from storage area',
        error: 'Failed to pick from storage area',
      },
      place: {
        title: 'Place in storage area',
        error: 'Failed to place from storage area',
      },
      pickAndPlace: {
        title: 'Pick and place',
        error: 'Failed to pick and place',
      },
    },
    failedDialog: {
      title: 'Move failed!',
      description: (fromStorageArea: string, toStorageArea: string) =>
        `Move from ${fromStorageArea} to ${toStorageArea}`,
    },
    moveTimberFromLogSorterBins: 'Move timber from bins',
  },
  home: {
    welcome: 'Welcome to',
    description: 'Next generation logistical system',
    username: 'Username',
    enterUsername: 'Enter your username to sign in',
    rememberUsername: 'Remember username',
    signInButton: 'Sign in',
  },
  unsavedChanges: {
    title: 'You have unsaved changes!',
    description: 'What do you want to do with them?',
  },
  audit: {
    dialog: {
      title: (name: string) => `Changes of ${name}`,
    },
    actions: {
      fetchShape: {
        title: 'Fetching shape audit',
        error: 'Failed to fetch shape audit',
      },
      fetchVehicle: {
        title: 'Fetching vehicle audit',
        error: 'Failed to fetch vehicle audit',
      },
      fetchProduct: {
        title: 'Fetching product audit',
        error: 'Failed to fetch product audit',
      },
      fetchPackage: {
        title: 'Fetching package audit',
        error: 'Failed to fetch package audit',
      },
      fetchPackaging: {
        title: 'Fetching packaging audit',
        error: 'Failed to fetch packaging audit',
      },
      fetchPackageType: {
        title: 'Fetching package type audit',
        error: 'Failed to fetch package type audit',
      },
      fetchProductGroup: {
        title: 'Fetching product group audit',
        error: 'Failed to fetch product group audit',
      },
      fetchDashboard: {
        title: 'Fetching dashboard audit',
        error: 'Failed to fetch dashboard audit',
      },
      fetchProductionOrder: {
        title: 'Fetching production order audit',
        error: 'Failed to fetch production order audit',
      },
      fetchInboundDelivery: {
        title: 'Fetching inbound delivery audit',
        error: 'Failed to fetch inbound delivery audit',
      },
      fetchOutboundDelivery: {
        title: 'Fetching outbound delivery audit',
        error: 'Failed to fetch outbound delivery audit',
      },
    },
  },
  packages: {
    actions: {
      fetchMany: {
        title: 'Fetching packages',
        error: 'Failed to fetch packages',
      },
      create: {
        title: 'Creating package',
        error: 'Failed to create package',
      },
      update: {
        title: 'Updating package',
        error: 'Failed to update package',
      },
      delete: {
        title: 'Deleting package',
        error: 'Failed to delete package',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete package ${name}`,
      description: 'Are you sure you want to delete this package?',
      deleteButton: 'Yes, delete package',
    },
    packages: 'Packages',
    addNewPackage: 'Add package',
    editPackage: 'Edit package',
    groups: {
      core: 'Core',
      physical: 'Physical',
      nominal: 'Nominal',
      statistics: 'Statistics',
    },
    layoutMode: 'Layout',
    layoutModes: {
      fieldset: 'Fieldsets',
      accordion: 'Accordion',
    },
    showDialogOptions: 'Show dialog options',
    hideDialogOptions: 'Hide dialog options',
    weightExceedsMaxWarning: 'Total weight exceeds the maximum allowed weight for one or more occupied storage places.',
    heightExceedsMaxWarning: 'Total height exceeds the maximum allowed height for one or more occupied storage places.',
    stackLevelExceedsMaxWarning: 'Stack level exceeds the maximum stack count for one or more occupied storage places.',
    placementOverlapWarning: 'Selected storage places are already occupied by another package at this stack level.',
    placesNotContiguousWarning: 'Occupied storage places are not contiguous.',
    occupiedPlacesNotContiguousWarning: 'Occupied storage places are not contiguous.',
    moveDialog: 'Move dialog',
    dragGroup: 'Drag',
    moveGroupUp: 'Move group up',
    moveGroupDown: 'Move group down',
    resizeGroupHeight: 'Resize group height',
    resizeGroupWidth: 'Resize group width',
    fields: {
      number: 'Number',
      externalNumber: 'External number',
      packageStorageArea: 'Package storage area',
      anchorStoragePlace: 'Anchor storage place',
      occupiedStoragePlaces: 'Occupied storage places',
      stackLevel: 'Stack level',
      accessibilityRating: 'Accessibility rating',
      nominalWeight: 'Nominal weight',
      weight: 'Weight',
      nominalHeight: 'Nominal height',
      height: 'Height',
      nominalWidth: 'Nominal width',
      width: 'Width',
      nominalLength: 'Nominal length',
      length: 'Length',
      nominalVolume: 'Nominal volume',
      volume: 'Volume',
      nominalRunningMeters: 'Nominal running meters',
      runningMeters: 'Running meters',
      statusValidFrom: 'Status valid from',
      statusValidTo: 'Status valid to',
      comment: 'Comment',
      timesMovedCount: 'Times moved',
      distanceTravelledKm: 'Distance travelled',
      timeCarried: 'Time carried',
      weightedDistanceTravelledKm: 'Weighted distance travelled',
      createdAt: 'Created',
      piecesCount: 'Pieces',
      layers: 'Layers',
      piecesPerLayer: 'Pieces per layer',
      spacerHeight: 'Spacer height',
      composition: 'Composition',
    },
    compositions: {
      Empty: 'Empty',
      Uniform: 'Uniform',
      Mixed: 'Mixed',
    },
    panel: {
      pickupTitle: 'Pick up',
      placementTitle: 'Put down',
      noNearbyPackages: 'No packages nearby',
      noNearbyPlaces: 'No storage places nearby',
      number: 'Number',
      accessibilityRating: 'Accessibility',
      distance: 'Distance',
      accessible: 'Accessible',
      access: 'Access',
      reason: 'Reason',
      yes: 'Yes',
      no: 'No',
      front: 'Front',
      right: 'Right',
      back: 'Back',
      left: 'Left',
      pickup: 'Pick up',
      putdown: 'Put down',
      pickupLimitReached: 'Pick-up limit reached',
      carriedPackages: 'Carried packages',
      nothingCarried: 'No packages carried',
      occupiesPlaces: (count: number) => `Occupies ${count} place(s)`,
      doesNotFit: 'Package does not fit in the available places',
      notInAccessDirection: 'Package is not in the access direction',
      pickupBlockedReasonPlaceNotAccessible:
        'The selected storage place does not allow pickup from any side',
      pickupBlockedReasonWrongAccessSide: 'Approach the package from an accessible side',
      pickupBlockedReasonPackageOrientationMismatch:
        'Vehicle pickup direction does not match the package orientation',
      approachAccessibleSide: 'Approach the place from an accessible side',
    },
    details: {
      title: 'Package details',
      summary: 'Summary',
      dimensions: 'Volume & dimensions',
      contents: 'Contents',
      noContents: 'No contents',
      status: 'Status',
      packageType: 'Package type',
      storageArea: 'Storage area',
      quality: 'Quality',
      age: 'Age',
      ageDays: (days: number) => `${days} day(s)`,
      product: 'Product',
      productCode: 'Product code',
      productGroup: 'Product group',
      pieces: 'Pieces',
      thickness: 'Thickness',
      nominalThickness: 'Nominal thickness',
      moisture: 'Moisture',
      partNumber: 'Part number',
      codes: 'Codes',
      resize: 'Resize',
    },
    contentTable: {
      mappingTrace: 'Mapping trace',
      hints: {
        physical: 'Actual measured values of the content row. Weight in kg (total of the row), thickness, width and length in mm. Volume (m³) and running meters (m) are calculated from pieces and dimensions.',
        nominal: 'Specified target values of the content row. Weight in kg (total of the row), thickness, width and length in mm. Volume (m³) and running meters (m) are calculated from pieces and dimensions.',
        moisture: 'Moisture content in %.',
        quality: 'Quality grade of the content. Taken from the product by default.',
        partNumber: 'Identifier of this part within the package.',
        mappingTrace: 'Product mapping that produced this content.',
        codes: 'Identification codes, e.g. barcodes, of this content. Click to edit.',
      },
      inputs: {
        weight: 'Weight (kg)',
        thickness: 'Thickness (mm)',
        width: 'Width (mm)',
        length: 'Length (mm)',
        volume: 'Volume (m³), calculated when pieces, thickness, width and length are set',
        runningMeters: 'Running meters (m), calculated when pieces and length are set',
      },
    },
  },
  packaging: {
    actions: {
      fetchMany: {
        title: 'Fetching packaging',
        error: 'Failed to fetch packaging',
      },
      create: {
        title: 'Creating packaging',
        error: 'Failed to create packaging',
      },
      update: {
        title: 'Updating packaging',
        error: 'Failed to update packaging',
      },
      delete: {
        title: 'Deleting packaging',
        error: 'Failed to delete packaging',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete packaging ${name}`,
      description: 'Are you sure you want to delete this packaging?',
      deleteButton: 'Yes, delete packaging',
    },
    packaging: 'Packaging',
    addNewPackaging: 'Add packaging',
    editPackaging: 'Edit packaging',
  },
  packageTypes: {
    actions: {
      fetchMany: {
        title: 'Fetching package types',
        error: 'Failed to fetch package types',
      },
      create: {
        title: 'Creating package type',
        error: 'Failed to create package type',
      },
      update: {
        title: 'Updating package type',
        error: 'Failed to update package type',
      },
      delete: {
        title: 'Deleting package type',
        error: 'Failed to delete package type',
        statusCodes: {},
      },
    },
    deleteDialog: {
      header: (name: string) => `Delete package type ${name}`,
      description: 'Are you sure you want to delete this package type?',
      deleteButton: 'Yes, delete package type',
    },
    packageTypes: 'Package type',
    addNewPackageType: 'Add package type',
    editPackageType: 'Edit package type',
  },
  units: {
    millimeters: 'mm',
    centimeters: 'cm',
    meters: 'm',
    cubicMeter: 'm³',
    kilogram: 'kg',
    ton: 'ton',
    percent: '%',
    kgPerM3: 'kg/m3',
  },
  coils: {
    actions: {
      fetchMany: {
        title: 'Fetching coils',
        error: 'Failed to fetch coils',
      },
      fetch: {
        title: 'Fetching coil',
        error: 'Failed to fetch coil',
      },
      create: {
        title: 'Creating coil',
        error: 'Failed to create coil',
      },
      update: {
        title: 'Updating coil',
        error: 'Failed to update coil',
      },
      delete: {
        title: 'Deleting coil',
        error: 'Failed to delete coil',
      },
      undoMove: {
        title: 'Undo coil move',
        error: 'Failed to undo coil move',
      },
    },

    page: {
      title: 'Coils',
      notFound: 'No coils found',
      loading: 'Loading coils',
    },

    deleteDialog: {
      header(name: string) {
        return `Delete coil ${name}`;
      },
      description: 'Are you sure you want to delete this coil? This action cannot be undone.',
      deleteButton: 'Yes delete coil',
    },

    editDialog: {
      create: {
        title: 'Create new coil',
      },
      edit: {
        title: 'Edit coil',
      },
    },

    slots: 'Number of slots',
    slotWidth: 'Slot width',
    maxHeight: 'Max height',
    maxLevels: 'Max levels',
    diameter: 'Diameter',
    depth: 'Depth',
    product: 'Product',
    storageArea: 'Storage area',
    position: 'Position',
    level: 'Level',
    maxCoilDiameter: 'Max coil diameter',

    moveDialog: {
      title: 'Move coil',
    },

    unableToDrawCoils: (coils: string[]) => {
      if (coils.length === 1) {
        return `${coils[0]} is on a invalid position inside the storage area`;
      } else {
        return 'Many coils have invalid positions inside the storage area';
      }
    },
  },
  logYard: {
    moveDialog: {
      title: 'Move logs',
    },
    logs: 'logs',
  },
  bulk: {
    actions: {
      fetch: {
        title: 'Fetching bulk',
        error: 'Failed to fetch bulk',
      },
      create: {
        title: 'Creating bulk',
        error: 'Failed to create bulk',
      },
      update: {
        title: 'Updating bulk',
        error: 'Failed to update bulk',
      },
      delete: {
        title: 'Deleting bulk',
        error: 'Failed to delete bulk',
      },
      undoMove: {
        title: 'Undo bulk move',
        error: 'Failed to undo bulk move',
      },
    },
    maxWeight: 'Max weight',
    maxVolume: 'Max volume',

    bulkUnit(value?: Unit | null): string {
      switch (value) {
        case Unit.Kilogram:
          return 'kilogram';
        case Unit.CubicMeter:
          return 'cubic meters';
        case Unit.LongTon:
          return 'long tons';
        case Unit.MetricTon:
          return 'metric tons';
        case Unit.Pound:
          return 'pounds';
        case Unit.ShortTon:
          return 'short tons';
        default:
          return '';
      }
    },
    bulkUnitShort(value?: Unit | null): string {
      switch (value) {
        case Unit.MetricTon:
          return 't';
        case Unit.Kilogram:
          return 'kg';
        case Unit.CubicMeter:
          return 'm³';
        case Unit.LongTon:
          return 'l.tn';
        case Unit.Pound:
          return 'lbs';
        case Unit.ShortTon:
          return 'tn';
        default:
          return '';
      }
    },
    moveDialog: {
      title: 'Move bulk',
    },
  },
  dashboard: {
    actions: {
      fetchMany: {
        title: 'Fetching dashboards',
        error: 'Failed to fetch dashboards',
      },
      fetch: {
        title: 'Fetching dashboard',
        error: 'Failed to fetch dashboard',
      },
      create: {
        title: 'Creating dashboard',
        error: 'Failed to create dashboard',
      },
      update: {
        title: 'Updating dashboard',
        error: 'Failed to update dashboard',
      },
      delete: {
        title: 'Deleting dashboard',
        error: 'Failed to delete dashboard',
        statusCodes: {
          409: 'Failed to delete dashboard',
        },
      },
    },
    listPage: {
      title: 'Dashboards',
      noFound: 'No dashboard found',
      loading: 'Loading dashboards...',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete dashboard ${name}`;
      },
      description: 'Are you sure you want to delete this dashboard? This action cannot be undone.',
      deleteButton: 'Yes delete dashboard',
    },
    editPage: {
      editTitle: 'Edit dashboard',
      createTitle: 'Create dashboard',
    },
    editComponentDialog: {
      editTitle: 'Edit component',
      addTitle: 'Add component',
    },
    productGroupTable: {
      title: 'Stock per product group',
    },
    productTable: {
      title: 'Stock per product',
    },
    productGroupDiagram: {
      title: 'Stock per product group',
    },
    noDashboards: 'No dashboards available',
  },
  overview: {
    title: 'Overview',
  },
  releaseNotes: {
    actions: {
      fetchLatest: {
        title: 'Fetching latest release note',
        error: 'Failed to fetch latest release note',
      },
      fetchMany: {
        title: 'Fetching release notes',
        error: 'Failed to fetch release notes',
      },
    },
  },
  keybinds: {
    action: 'Action',
    keybind: 'Keybind',
  },
  reportPage: {
    selectReport: 'Select report',
    showReport: 'Show report',
    selectExportFormat: 'Select export format',
    exportReport: 'Export report',
    exportError: 'Failed to export report',
    showError: 'Failed to show report',
  },
  mapTable: {
    col: {
      name: 'Name',
      type: 'Type',
    },
  },
  dataTable: {
    filterBy: 'Filter by',
    clearFilters: 'Clear filters',
    showing: 'showing',
    of: 'of',
    filtered: 'filtered',
  },
  primeVueLocale: {
    startsWith: 'Starts with',
    contains: 'Contains',
    notContains: 'Does not contain',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    noFilter: 'No Filter',

    lt: 'Less than',
    lte: 'Less or equal',
    gt: 'Greater than',
    gte: 'Greater or equal',

    dateIs: 'Date is',
    dateIsNot: 'Date is not',
    dateBefore: 'Date is before',
    dateAfter: 'Date is after',

    clear: 'Clear',
    apply: 'Apply',

    matchAll: 'Match All',
    matchAny: 'Match Any',
    addRule: 'Add Rule',
    removeRule: 'Remove Rule',

    accept: 'Accept',
    reject: 'Reject',

    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    completed: 'Completed',
    pending: 'Pending',

    fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB'],

    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],

    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],

    chooseYear: 'Choose Year',
    chooseMonth: 'Choose Month',
    chooseDate: 'Choose Date',

    prevDecade: 'Previous Decade',
    nextDecade: 'Next Decade',
    prevYear: 'Previous Year',
    nextYear: 'Next Year',
    prevMonth: 'Previous Month',
    nextMonth: 'Next Month',
    prevHour: 'Previous Hour',
    nextHour: 'Next Hour',
    prevMinute: 'Previous Minute',
    nextMinute: 'Next Minute',
    prevSecond: 'Previous Second',
    nextSecond: 'Next Second',

    am: 'AM',
    pm: 'PM',
    today: 'Today',
    weekHeader: 'Wk',

    firstDayOfWeek: 0,
    showMonthAfterYear: false,
    dateFormat: 'mm/dd/yy',

    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',

    emptyFilterMessage: 'No results found',
    searchMessage: '{0} results available',
    selectionMessage: '{0} items selected',
    emptySelectionMessage: 'No items selected',
    emptySearchMessage: 'No results found',
    emptyMessage: 'No data available',

    fileChosenMessage: '{0} files',
    noFileChosenMessage: 'No file chosen',
  },
  delivery: {
    plannedStart: 'Planned start',
    plannedFinish: 'Planned end',
    actualStart: 'Actual start',
    actualFinish: 'Actual end',
  },
  inboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Fetching inbound deliveries',
        error: 'Failed to fetch inbound deliveries',
      },
      fetch: {
        title: 'Fetching inbound deliveries',
        error: 'Failed to fetch inbound deliveries',
      },
      create: {
        title: 'Creating inbound deliveries',
        error: 'Failed to create inbound deliveries',
      },
      update: {
        title: 'Updating inbound deliveries',
        error: 'Failed to update inbound deliveries',
      },
      delete: {
        title: 'Deleting inbound deliveries',
        error: 'Failed to delete inbound deliveries',
        statusCodes: {
          409: 'Failed to delete inbound deliveries',
        },
      },
      transaction: {
        title: 'Creating inbound delivery transaction',
        error: 'Failed to create inbound delivery transaction',
      },
      fetchTransactions: {
        title: 'Fetch transactions for delivery',
        error: 'Failed to fetch transactions for delivery',
      },
    },
    listPage: {
      title: 'Inbound deliveries',
      noFound: 'No delivery found',
      loading: 'Loading deliveries...',
      transactions: 'Show transactions',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete delivery ${name}`;
      },
      description: 'Are you sure you want to delete this delivery?',
      deleteButton: 'Yes delete delivery',
    },
    editPage: {
      editTitle: 'Edit inbound delivery',
      createTitle: 'Create inbound delivery',
      rowsTitle: 'Rows',
      addRow: 'Add row',
      noRows: 'No rows added',
      productRequired: 'Product is required for all inbound delivery rows.',
      rowWeight: 'Weight',
      rowVolume: 'Volume',
      rowDeliveredWeight: 'Delivered weight',
      rowDeliveredVolume: 'Delivered volume',
    },
    transactionDialog: {
      openButton: 'Add inbound transaction',
      title: 'Add inbound transaction',
      filterMode: 'Delivery filter',
      filterMatchingStorageArea: 'Matching storage area',
      filterAllDeliveries: 'All deliveries',
      delivery: 'Delivery',
      deliveryRow: 'Delivery row',
      selectDelivery: 'Select delivery',
      selectDeliveryRow: 'Select delivery row',
      selectStorageArea: 'Select storage area',
      deliveryRequired: 'Delivery is required.',
      deliveryRowRequired: 'Delivery row is required.',
      storageAreaRequired: 'Storage area is required.',
      weightRequired: 'Weight is required.',
    },
    transactionListDialog: {
      title: 'Transactions for the delivery',
      noFound: 'No transactions found',
      loading: 'Loading transactions',
      transactionTime: 'Timestamp',
    },
  },
  outboundDelivery: {
    actions: {
      fetchMany: {
        title: 'Fetching outbound deliveries',
        error: 'Failed to fetch outbound deliveries',
      },
      fetch: {
        title: 'Fetching outbound deliveries',
        error: 'Failed to fetch outbound deliveries',
      },
      create: {
        title: 'Creating outbound deliveries',
        error: 'Failed to create outbound deliveries',
      },
      update: {
        title: 'Updating outbound deliveries',
        error: 'Failed to update outbound deliveries',
      },
      delete: {
        title: 'Deleting outbound deliveries',
        error: 'Failed to delete outbound deliveries',
        statusCodes: {
          409: 'Failed to delete outbound deliveries',
        },
      },
      transaction: {
        title: 'Creating outbound delivery transaction',
        error: 'Failed to create outbound delivery transaction',
      },
      fetchTransactions: {
        title: 'Fetch transactions for delivery',
        error: 'Failed to fetch transactions for delivery',
      },
    },
    listPage: {
      title: 'Outbound deliveries',
      noFound: 'No delivery found',
      loading: 'Loading deliveries...',
      transactions: 'Show transactions',
    },
    deleteDialog: {
      header(name: string) {
        return `Delete delivery ${name}`;
      },
      description: 'Are you sure you want to delete this delivery?',
      deleteButton: 'Yes delete delivery',
    },
    editPage: {
      editTitle: 'Edit outbound delivery',
      createTitle: 'Create outbound delivery',
      rowsTitle: 'Rows',
      addRow: 'Add row',
      noRows: 'No rows added',
      productRequired: 'Product is required for all outbound delivery rows.',
      rowWeight: 'Weight',
      rowVolume: 'Volume',
      rowDeliveredWeight: 'Delivered weight',
      rowDeliveredVolume: 'Delivered volume',
    },
    transactionDialog: {
      openButton: 'Add outbound transaction',
      title: 'Add outbound transaction',
      filterMode: 'Delivery filter',
      filterMatchingStorageArea: 'Matching storage area',
      filterAllDeliveries: 'All deliveries',
      delivery: 'Delivery',
      deliveryRow: 'Delivery row',
      selectDelivery: 'Select delivery',
      selectDeliveryRow: 'Select delivery row',
      selectStorageArea: 'Select storage area',
      deliveryRequired: 'Delivery is required.',
      deliveryRowRequired: 'Delivery row is required.',
      storageAreaRequired: 'Storage area is required.',
      weightRequired: 'Weight is required.',
    },
    transactionListDialog: {
      title: 'Transactions for the delivery',
      noFound: 'No transactions found',
      loading: 'Loading transactions',
      transactionTime: 'Timestamp',
    },
  },
};
